import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';

/**
 * Mire de test vidéoprojecteur.
 * Affichée sur la fenêtre vitrine pour valider la config avant une séance :
 * - Couleurs (RGB + noir/blanc)
 * - Résolution & ratio détectés
 * - Lisibilité (3 tailles de texte)
 * - Grille de calibration + cibles bord d'écran
 * - Position sur quel écran
 */
export default function RvpTestPattern({ onClose }) {
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [screenInfo, setScreenInfo] = useState({ x: window.screenX, y: window.screenY });

  useEffect(() => {
    const onResize = () => {
      setDims({ w: window.innerWidth, h: window.innerHeight });
      setScreenInfo({ x: window.screenX, y: window.screenY });
    };
    window.addEventListener('resize', onResize);
    const interval = setInterval(onResize, 500);
    return () => { window.removeEventListener('resize', onResize); clearInterval(interval); };
  }, []);

  const ratio = dims.w && dims.h ? (dims.w / dims.h).toFixed(3) : '?';
  const ratioLabel = Math.abs(dims.w / dims.h - 16 / 9) < 0.02 ? '16:9' : Math.abs(dims.w / dims.h - 16 / 10) < 0.02 ? '16:10' : Math.abs(dims.w / dims.h - 4 / 3) < 0.02 ? '4:3' : 'custom';

  return (
    <div className="fixed inset-0 z-[300] overflow-hidden bg-black" data-testid="rvp-test-pattern">
      {/* Cibles aux 4 coins pour vérifier qu'aucun bord n'est coupé */}
      <div className="absolute top-2 left-2 w-16 h-16 border-2 border-red-500 flex items-center justify-center text-red-500 font-mono-spec text-[10px] font-bold">↖ NW</div>
      <div className="absolute top-2 right-2 w-16 h-16 border-2 border-red-500 flex items-center justify-center text-red-500 font-mono-spec text-[10px] font-bold">↗ NE</div>
      <div className="absolute bottom-2 left-2 w-16 h-16 border-2 border-red-500 flex items-center justify-center text-red-500 font-mono-spec text-[10px] font-bold">↙ SW</div>
      <div className="absolute bottom-2 right-2 w-16 h-16 border-2 border-red-500 flex items-center justify-center text-red-500 font-mono-spec text-[10px] font-bold">↘ SE</div>

      {/* Grille centrale */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
        backgroundSize: '80px 80px',
      }} />

      {/* Croix de centrage */}
      <div className="absolute top-1/2 left-0 right-0 h-px bg-red-500/40" />
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-red-500/40" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-2 border-red-500 rounded-full" />

      {/* Bandes de couleur RGB */}
      <div className="absolute top-24 left-1/2 -translate-x-1/2 flex gap-1">
        <div className="w-16 h-16 bg-white flex items-center justify-center text-black text-[10px] font-bold">255</div>
        <div className="w-16 h-16 bg-red-600 flex items-center justify-center text-white text-[10px] font-bold">R</div>
        <div className="w-16 h-16 bg-green-500 flex items-center justify-center text-white text-[10px] font-bold">G</div>
        <div className="w-16 h-16 bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold">B</div>
        <div className="w-16 h-16 bg-yellow-400 flex items-center justify-center text-black text-[10px] font-bold">Y</div>
        <div className="w-16 h-16 bg-fuchsia-500 flex items-center justify-center text-white text-[10px] font-bold">M</div>
        <div className="w-16 h-16 bg-cyan-400 flex items-center justify-center text-black text-[10px] font-bold">C</div>
        <div className="w-16 h-16 bg-black border border-white flex items-center justify-center text-white text-[10px] font-bold">0</div>
      </div>

      {/* Panneau infos + lisibilité */}
      <div className="absolute inset-x-0 bottom-24 flex flex-col items-center gap-4">
        <div className="text-center">
          <div className="font-serif-editorial text-white text-[64px] leading-none">Lisible ?</div>
          <div className="mt-2 text-white text-[24px]">Texte moyen — visible depuis le fond de la salle ?</div>
          <div className="mt-1 text-white/70 text-[13px]">Petit texte — les petits caractères sont-ils nets ?</div>
        </div>
      </div>

      {/* Bandeau infos système */}
      <div className="absolute top-24 left-6 bg-black/80 border border-white/20 rounded-lg p-3 text-white font-mono-spec text-[11px] space-y-1 backdrop-blur-md">
        <div className="text-red-400 font-bold text-[10px] uppercase tracking-wider mb-2">Mire de test · Projecteur</div>
        <div>Résolution : <span className="text-emerald-300 font-bold">{dims.w} × {dims.h}</span></div>
        <div>Ratio : <span className="text-emerald-300 font-bold">{ratio}</span> <span className="text-white/60">({ratioLabel})</span></div>
        <div>Position fenêtre : <span className="text-sky-300">x={screenInfo.x}, y={screenInfo.y}</span></div>
        <div>Fullscreen : <span className={document.fullscreenElement ? 'text-emerald-300' : 'text-amber-300'}>{document.fullscreenElement ? 'OUI ✓' : 'NON'}</span></div>
        <div>DPR : <span className="text-white/80">{window.devicePixelRatio}</span></div>
      </div>

      {/* Bouton fermer */}
      <button
        onClick={onClose}
        data-testid="rvp-test-pattern-close"
        className="absolute top-24 right-6 inline-flex items-center gap-2 h-11 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13px] font-bold transition shadow-lg"
      >
        <Check size={15} /> Config OK · Fermer
      </button>
    </div>
  );
}
